import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import FormBuilder, {checkConstraintsFactory} from '@s-ui/react-form-builder'
import MoleculeNotification from '@s-ui/react-molecule-notification'
import FormConditionsAndPrivacyCheckbox from '@adv-mt/mt-form-conditions-and-privacy-checkbox'

import {useFormUserContactConfiguration} from './useFormUserContactConfiguration.js'
import {isValidForm} from './utils.js'

const BASE_CLASS_NAME = 'mt-FormUserContact'
const NO_OP = () => {}

const FormUserContact = ({
  buttonColor = atomButtonColors.ACCENT,
  buttonText,
  currentUser = {},
  isPrivacyChecked: isPrivacyCheckedInitialValue = false,
  onFormFieldsError = NO_OP,
  onFormFieldValueChange = NO_OP,
  onSubmit = NO_OP,
  secondaryButton
}) => {
  const {i18n} = useContext(Context)

  const {formUserContactConfiguration, formErrorTypes, formFieldIds, initFields, SUIComponentsConfig} =
    useFormUserContactConfiguration({currentUser})

  const [validatedErrors, setValidatedErrors] = useState({})
  const [fields, setFields] = useState(initFields)
  const [isLoading, setIsLoading] = useState(false)
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(isPrivacyCheckedInitialValue)
  const [isPrivacyError, setIsPrivacyError] = useState(false)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setFields(initFields)
  }, [initFields])

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formUserContactConfiguration)(checkParams)

    setErrors(_errors)
    return _errors
  }

  const onBlur = fieldId => {
    onFormFieldValueChange({fieldId})
    checkConstraints({fieldId})
  }

  const onChange = newFields => {
    setFields({...newFields})
  }

  const handleOnSubmit = async () => {
    setIsLoading(true)
    setHasError(false)

    const errors = checkConstraints()

    if (!isPrivacyChecked) {
      setIsPrivacyError(true)
    }

    if (isValidForm(errors) && isPrivacyChecked) {
      const {__FIELD_CHANGED__, ...restOfForm} = fields

      const submitResponse = await onSubmit({fields: restOfForm})

      if (submitResponse?.isError) {
        setIsLoading(false)
        setHasError(true)
      }
    } else {
      setIsLoading(false)
      onFormFieldsError({
        errorType: formErrorTypes.fieldError,
        formErrors: errors,
        isPrivacyChecked
      })
    }
  }

  return (
    <>
      <div className={`${BASE_CLASS_NAME}-form`}>
        <FormBuilder
          initFields={initFields}
          json={formUserContactConfiguration}
          onBlur={onBlur}
          onChange={onChange}
          errors={validatedErrors}
          setErrors={setValidatedErrors}
        >
          {SUIComponentsConfig}
        </FormBuilder>
      </div>

      <div className={`${BASE_CLASS_NAME}-privacyCheckbox`}>
        {isPrivacyCheckedInitialValue ? null : (
          <FormConditionsAndPrivacyCheckbox
            checked={isPrivacyChecked}
            isError={isPrivacyError}
            onChange={value => {
              onFormFieldValueChange({fieldId: formFieldIds.privacyCheck})
              setIsPrivacyChecked(value)
              setIsPrivacyError(false)
            }}
          />
        )}
        {hasError && (
          <div className={`${BASE_CLASS_NAME}-notification`}>
            <MoleculeNotification
              autoClose="manual"
              type="warning"
              showCloseButton={false}
              roundedCorners="m"
              show={hasError}
            >
              <span>{i18n.t('COMMON.SOMETHING_WENT_WRONG')}</span>
            </MoleculeNotification>
          </div>
        )}
      </div>
      <div className={`${BASE_CLASS_NAME}-buttonGroup`}>
        <div className={`${BASE_CLASS_NAME}-button`}>
          <AtomButton
            onClick={() => handleOnSubmit()}
            size={atomButtonSizes.LARGE}
            fullWidth
            color={buttonColor}
            isLoading={isLoading}
            shape={atomButtonShapes.CIRCULAR}
          >
            {buttonText || i18n.t('COMMON.CONTINUE')}
          </AtomButton>
        </div>
        {secondaryButton ? (
          <div className={`${BASE_CLASS_NAME}-buttonSecondary`}>
            <AtomButton
              design={atomButtonDesigns.LINK}
              onClick={secondaryButton.handleOnClick}
              size={atomButtonSizes.LARGE}
              disabled={isLoading}
            >
              {secondaryButton.text || i18n.t('COMMON.GO_BACK')}
            </AtomButton>
          </div>
        ) : null}
      </div>
    </>
  )
}

FormUserContact.displayName = 'FormUserContact'
FormUserContact.propTypes = {
  buttonColor: PropTypes.oneOf(Object.values(atomButtonColors)),
  buttonText: PropTypes.string,
  currentUser: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string
  }),
  isPrivacyChecked: PropTypes.bool,
  onFormFieldsError: PropTypes.func,
  onFormFieldValueChange: PropTypes.func,
  onSubmit: PropTypes.func,
  secondaryButton: PropTypes.shape({
    handleOnClick: PropTypes.func,
    text: PropTypes.string
  })
}

export default FormUserContact
